/**
 * Shared Herald json-render catalog + registry for chat rendering and SSR smoke tests.
 * Used by {@link HeraldSpecRenderer} and {@link heraldSsrSmokeEntry}.
 */
import { defineCatalog } from "@json-render/core";
import type { ComponentRegistry } from "@json-render/react";
import { defineRegistry } from "@json-render/react";
import { schema } from "@json-render/react/schema";
import { shadcnComponents } from "@json-render/shadcn";
import { shadcnComponentDefinitions } from "@json-render/shadcn/catalog";
import {
  ChatAreaChartFromProps,
  ChatAreaChartSpec,
} from "@/components/ui/area-chart/ChatAreaChartSpec";
import {
  ChatBarChartFromProps,
  ChatBarChartSpec,
  ChatStackedBarChartFromProps,
  ChatStackedBarChartSpec,
} from "@/components/ui/bar-chart/ChatBarChartSpec";
import {
  ChatButtonFromProps,
  ChatButtonSpec,
} from "@/components/ui/button/ChatButtonSpec";
import {
  ChatBlockquoteFromProps,
  ChatBlockquoteSpec,
} from "@/components/ui/chat/blockquote/ChatBlockquoteSpec";
import {
  ChatMarkdownFromProps,
  ChatMarkdownSpec,
} from "@/components/ui/chat/ChatMarkdownSpec";
import {
  ChatCalloutFromProps,
  ChatCalloutSpec,
} from "@/components/ui/chat/callout/ChatCalloutSpec";
import {
  ChatDataListFromProps,
  ChatDataListSpec,
} from "@/components/ui/chat/data-list/ChatDataListSpec";
import {
  ChatHorizontalBarFromProps,
  ChatHorizontalBarSpec,
} from "@/components/ui/chat/horizontal-bar/ChatHorizontalBarSpec";
import {
  ChatImageFromProps,
  ChatImageSpec,
} from "@/components/ui/chat/image/ChatImageSpec";
import {
  ChatLinkFromProps,
  ChatLinkSpec,
} from "@/components/ui/chat/link/ChatLinkSpec";
import {
  ChatMetricFromProps,
  ChatMetricGroupFromProps,
  ChatMetricGroupSpec,
  ChatMetricSpec,
} from "@/components/ui/chat/metric/ChatMetricSpec";
import {
  ChatSuggestionChipsFromProps,
  ChatSuggestionChipsSpec,
} from "@/components/ui/chat/suggestion-chips/ChatSuggestionChipsSpec";
import {
  ChatTableFromProps,
  ChatTableSpec,
} from "@/components/ui/chat/table/ChatTableSpec";
import {
  ChatTimelineFromProps,
  ChatTimelineSpec,
} from "@/components/ui/chat/timeline/ChatTimelineSpec";
import {
  ChatComposedChartFromProps,
  ChatComposedChartSpec,
} from "@/components/ui/composed-chart/ChatComposedChartSpec";
import {
  ChatLineChartFromProps,
  ChatLineChartSpec,
} from "@/components/ui/line-chart/ChatLineChartSpec";
import {
  ChatPieDonutChartFromProps,
  ChatPieDonutChartSpec,
} from "@/components/ui/pie-chart/ChatPieDonutChartSpec";
import {
  ChatRadarChartFromProps,
  ChatRadarChartSpec,
} from "@/components/ui/radar-chart/ChatRadarChartSpec";
import {
  ChatRadialBarChartFromProps,
  ChatRadialBarChartSpec,
} from "@/components/ui/radial-bar-chart/ChatRadialBarChartSpec";
import {
  ChatSparkChartRowFromProps,
  ChatSparkChartRowSpec,
} from "@/components/ui/spark-chart/ChatSparkChartRowSpec";
import { withLinkableStringProps } from "@/lib/chat/withLinkableStringProps";

const rendererCatalog = defineCatalog(schema, {
  components: {
    Stack: shadcnComponentDefinitions.Stack,
    Grid: shadcnComponentDefinitions.Grid,
    Card: shadcnComponentDefinitions.Card,
    Radio: shadcnComponentDefinitions.Radio,
    Select: shadcnComponentDefinitions.Select,
    Skeleton: shadcnComponentDefinitions.Skeleton,
    Progress: shadcnComponentDefinitions.Progress,
    Badge: shadcnComponentDefinitions.Badge,
    Table: ChatTableSpec,
    AreaChart: ChatAreaChartSpec,
    ComposedChart: ChatComposedChartSpec,
    LineChart: ChatLineChartSpec,
    BarChart: ChatBarChartSpec,
    StackedBarChart: ChatStackedBarChartSpec,
    HorizontalBar: ChatHorizontalBarSpec,
    Image: ChatImageSpec,
    PieChart: ChatPieDonutChartSpec,
    RadarChart: ChatRadarChartSpec,
    RadialBarChart: ChatRadialBarChartSpec,
    SparkChartRow: ChatSparkChartRowSpec,
    Callout: ChatCalloutSpec,
    DataList: ChatDataListSpec,
    Timeline: ChatTimelineSpec,
    Metric: ChatMetricSpec,
    Quote: ChatBlockquoteSpec,
    MetricGroup: ChatMetricGroupSpec,
    Button: ChatButtonSpec,
    Link: ChatLinkSpec,
    Markdown: ChatMarkdownSpec,
    SuggestionChips: ChatSuggestionChipsSpec,
  },
  actions: {},
});

const { registry, handlers } = defineRegistry(rendererCatalog, {
  components: {
    Stack: shadcnComponents.Stack,
    Grid: shadcnComponents.Grid,
    Badge: shadcnComponents.Badge,
    AreaChart: ChatAreaChartFromProps,
    ComposedChart: ChatComposedChartFromProps,
    LineChart: ChatLineChartFromProps,
    BarChart: ChatBarChartFromProps,
    StackedBarChart: ChatStackedBarChartFromProps,
    HorizontalBar: ChatHorizontalBarFromProps,
    Image: ChatImageFromProps,
    PieChart: ChatPieDonutChartFromProps,
    RadarChart: ChatRadarChartFromProps,
    RadialBarChart: ChatRadialBarChartFromProps,
    SparkChartRow: ChatSparkChartRowFromProps,
    Button: ChatButtonFromProps,
    Link: ChatLinkFromProps,
    Callout: withLinkableStringProps(ChatCalloutFromProps, [
      "title",
      "content",
    ]),
    DataList: withLinkableStringProps(ChatDataListFromProps, [
      "items[].label",
      "items[].value",
    ]),
    Timeline: withLinkableStringProps(ChatTimelineFromProps, [
      "heading",
      "items[].title",
      "items[].description",
    ]),
    Card: shadcnComponents.Card,
    Metric: withLinkableStringProps(ChatMetricFromProps, [
      "label",
      "subtitle",
      "value",
    ]),
    MetricGroup: withLinkableStringProps(ChatMetricGroupFromProps, [
      "items[].label",
      "items[].subtitle",
      "items[].value",
    ]),
    Quote: ChatBlockquoteFromProps,
    Markdown: ChatMarkdownFromProps,
    Radio: shadcnComponents.Radio,
    Select: shadcnComponents.Select,
    Skeleton: shadcnComponents.Skeleton,
    Progress: shadcnComponents.Progress,
    Table: ChatTableFromProps,
    SuggestionChips: ChatSuggestionChipsFromProps,
  },
  actions: {},
});

/** @public json-render component registry shared by Herald chat and SSR smoke render. */
export const heraldRendererRegistry: ComponentRegistry = registry;

/** @public Action handler factory from {@link heraldRendererRegistry}. */
export const createHeraldRendererHandlers = handlers;

/** @public Sorted registry keys, including legacy components omitted from generated UI. */
export const heraldRendererComponentTypes = Object.keys(registry).sort();
